import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, AlertTriangle, Loader2, Upload as UploadIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { importService } from '@/services/api/import';

export default function Step5Upload({ sessionId, onDone }: { sessionId: string; onDone: () => void }) {
  const [progress, setProgress] = useState({
    state: 'uploading',
    byStatus: {} as Record<string, number>,
    totals: {} as Record<string, number>,
  });
  const cancelled = useRef(false);

  useEffect(() => {
    const poll = async () => {
      while (!cancelled.current) {
        try {
          const p = await importService.getProgress(sessionId);
          setProgress(p as any);
          if (p.state === 'done' || p.state === 'failed') return;
        } catch (e) {
          console.error('progress fetch failed', e);
        }
        await new Promise(r => setTimeout(r, 1500));
      }
    };
    void poll();
    return () => { cancelled.current = true; };
  }, [sessionId]);

  const uploaded = progress.byStatus.uploaded || 0;
  const skippedDup = progress.byStatus.skipped_dup || 0;
  const failed = progress.byStatus.failed || 0;
  const total = (progress.totals as any).included || (uploaded + skippedDup + failed + (progress.byStatus.detected || 0));
  const done = uploaded + skippedDup + failed;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const finished = progress.state === 'done' || progress.state === 'failed';

  return (
    <Card>
      <CardContent className="pt-8 pb-8 space-y-5 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
          {finished
            ? (failed > 0 ? <AlertTriangle className="h-7 w-7 text-amber-500" /> : <CheckCircle2 className="h-7 w-7 text-emerald-500" />)
            : <UploadIcon className="h-7 w-7 animate-pulse" />}
        </div>
        <div>
          <p className="text-base font-semibold">
            {finished ? (failed > 0 ? 'Done with errors' : 'All imported.') : 'Uploading to S3…'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {finished
              ? `${uploaded} uploaded, ${skippedDup} skipped (duplicate)${failed > 0 ? `, ${failed} failed` : ''}`
              : `${done} / ${total} files`}
          </p>
        </div>
        {!finished && <Progress value={pct} className="max-w-md mx-auto" />}

        <div className="flex justify-center gap-3 pt-2">
          {finished ? (
            <Button onClick={onDone} className="gap-2">
              Import more <UploadIcon className="h-4 w-4" />
            </Button>
          ) : (
            <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> please keep this tab open
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
